import { createSelector } from '@reduxjs/toolkit'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { selectCubeContext } from './CubeContext.ts'
import { filterCubeByPathAndGrade } from './CubeFiltering.ts'
import { taxaPathKey } from 'Util/Visualisation/TaxaPathKey.ts'
import type { FilteredBucketValues } from './CubeFiltering.ts'
import { createSelectorCache, getOrBuildSelector } from './SelectorCache.ts'
import { normTaxon } from '../../Transformer/Impl/TraitCommon.ts'

const EMPTY: FilteredBucketValues[] = []
const cache = createSelectorCache<FilteredBucketValues[]>()

/**
 * Shared, memoised cube filter — **the** fix for the slow zoom.
 *
 * The filtered result depends only on `(table, selectedBuckets, selectedPath, gradeThreshold,
 * countMode)`, none of which is sink-specific. Yet every cube-backed consumer
 * (`TaxaStackedBarSeries`, `TaxaColorMapAtLevel`, `MapSink`, `BetaDiversitySink`, …) used to call
 * `filterCubeByPathAndGrade` itself, so a single zoom ran ~10 independent full-cube scans —
 * which the VizPerf report showed dominating the click latency (`cube.filter` × 10, ~160ms each;
 * the stacked-bar "render" was mostly two of those scans, not paint).
 *
 * Routing all of them through this one memoised selector collapses that to a single scan per
 * `(sourceId, selectedPath, gradeThreshold)`; the rest hit the createSelector cache and get the
 * same array reference (which also lets each consumer's own createSelector stay memoised).
 *
 * `table`/`selectedBuckets`/`countMode` arrive via the memoised {@link selectCubeContext} input,
 * so a countMode toggle or a cube reload recomputes correctly. `selectedPath` is deliberately NOT
 * an input of `selectCubeContext`, so a zoom leaves `ctx` referentially stable and only this
 * selector (keyed on the path) re-runs. Returns `[]` until the cube is ready — callers gate on
 * `selectCubeContext(...).status` themselves.
 */
export function selectFilteredCube(
  state: typeof State,
  sourceId: SourceId,
  opts: { selectedPath: string[]; gradeThreshold: number }
): FilteredBucketValues[] {
  const traitName = state.visualization.sources[sourceId]?.shared?.natureKPIs?.traitName ?? ''
  const key = `${sourceId}|p=${taxaPathKey(opts.selectedPath)}|g=${opts.gradeThreshold}|t=${traitName}`
  const { selectedPath, gradeThreshold } = opts
  return getOrBuildSelector(cache, key, () =>
    createSelector(
      [
        (s: typeof State) => selectCubeContext(s, sourceId),
        (s: typeof State) => s.visualization.sources[sourceId]?.shared?.natureKPIs?.traitName ?? '',
        (s: typeof State) => s.visualization.traits?.traits ?? []
      ],
      (ctx, tName, allTraits): FilteredBucketValues[] => {
        if (ctx.status !== 'ready') return EMPTY
        const base = filterCubeByPathAndGrade({
          table: ctx.table,
          selectedBuckets: ctx.selectedBuckets,
          selectedPath,
          gradeThreshold,
          countMode: ctx.countMode
        })
        if (!tName) return base
        // Taxa (any group) carrying this exact trait; keep a row if any lineage taxon is one of them.
        const traitTaxa = new Set<string>()
        for (const t of allTraits) if (t.traitname === tName) traitTaxa.add(normTaxon(t.consensustaxon))
        if (traitTaxa.size === 0) return base
        return base
          .map((b) => ({ ...b, values: b.values.filter((v) => v.taxa.some((x) => traitTaxa.has(normTaxon(x ?? '')))) }))
          .filter((b) => b.values.length > 0)
      }
    )
  )(state)
}
